/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Products API Service
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { StripeProduct } from "@/types";
import {
  baseQueryWithReauth,
  CACHE_TIMES,
  TAG_TYPES,
} from "@/redux/api-config";

export const productsApi = createApi({
  reducerPath: "productsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [TAG_TYPES.PRODUCTS],
  // Products are relatively stable, cache longer
  keepUnusedDataFor: CACHE_TIMES.STABLE,
  endpoints: (builder) => ({
    getProductById: builder.query<{ data: StripeProduct }, string>({
      query: (id) => `products/${id}`,
      providesTags: (_result, _error, id) => [
        { type: TAG_TYPES.PRODUCTS, id },
        { type: TAG_TYPES.PRODUCTS, id: "LIST" },
      ],
      // Product details are very stable
      keepUnusedDataFor: CACHE_TIMES.STABLE,
    }),
    getProducts: builder.query<{ data: StripeProduct[] }, void>({
      query: () => "products",
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((product) => ({
                type: TAG_TYPES.PRODUCTS,
                id: product.id,
              })),
              { type: TAG_TYPES.PRODUCTS, id: "LIST" },
            ]
          : [{ type: TAG_TYPES.PRODUCTS, id: "LIST" }],
    }),
  }),
});

export const {
  useGetProductByIdQuery,
  useGetProductsQuery,
  useLazyGetProductByIdQuery,
} = productsApi;
